import {
  IResourceComponentsProps,
  useNavigation,
  useShow,
  useTranslate,
  useNotification,
} from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
  FormControl,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import React, { useEffect } from "react";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import Avatar from "@mui/material/Avatar";
import { Phone } from "lucide-react";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { getDuration } from "../../utils/getDuration";
import { useGeocoding } from "../../hooks/useGeocoding";
import { useParams } from "react-router-dom";
import { useAuth } from "../../contexts/auth-context";
import { canAccessUserData } from "../../utils/role-permissions";
import { IVSPlayer } from "../../components/IVSPlayer";
import axiosInstance from "../../api/axios-config";
import { getRecordingUrl } from "../../api/videos";
import { createLogger } from "../../utils/logger";

const logger = createLogger('MonitoringStream');

export const MonitoringAlertShow: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();
  const { goBack } = useNavigation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { id } = useParams();
  const { user: currentUser } = useAuth();
  const { open: openNotification } = useNotification();

  // Log component mount with navigation type detection
  const mountTimeRef = React.useRef(Date.now());
  React.useEffect(() => {
    const mountTime = mountTimeRef.current;
    const navType = (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming)?.type;
    logger.log('📺 MonitoringAlertShow MOUNTED', {
      alertId: id,
      navigationType: navType, // 'navigate', 'reload', 'back_forward', 'prerender'
      isDirectAccess: navType === 'navigate' || navType === 'reload',
      timestamp: new Date().toISOString()
    });
    return () => {
      logger.log('📺 MonitoringAlertShow UNMOUNTED', {
        alertId: id,
        totalLifetime: Date.now() - mountTime,
        timestamp: new Date().toISOString()
      });
    };
  }, [id]);

  // États pour le player IVS (déclarés avant useShow pour éviter l'erreur de référence)
  const [playerConnected, setPlayerConnected] = React.useState(false);
  const [playerError, setPlayerError] = React.useState<string | null>(null);
  const [showEndedView, setShowEndedView] = React.useState(false);
  const [retryCount, setRetryCount] = React.useState(0);
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);
  const [streamHasEnded, setStreamHasEnded] = React.useState(false); // Pour masquer immédiatement le player live

  // État pour le status de l'alerte
  const [alertStatus, setAlertStatus] = React.useState<'NEW' | 'ACCEPTED' | 'CLOSED' | 'CALLED'>('NEW');
  const [isUpdatingStatus, setIsUpdatingStatus] = React.useState(false);

  // États pour l'auto-refresh (similaire à MonitoringList)
  const [isPageVisible, setIsPageVisible] = React.useState(true);

  // États pour le replay
  const [replayUrl, setReplayUrl] = React.useState<string | null>(null);
  const [isLoadingReplay, setIsLoadingReplay] = React.useState(false);
  const [replayError, setReplayError] = React.useState<string | null>(null);
  const [replayAttempted, setReplayAttempted] = React.useState<string | null>(null); // Track streamId for which replay was attempted

  // Track polling state for logging
  const lastPollingStateRef = React.useRef<string>('');

  const { queryResult } = useShow({
    resource: "alert",
    id,
    queryOptions: {
      refetchInterval: (data): number | false => {
        const record = data?.data;
        const isBroadcasting = record?.isBroadcasting;
        const hasPlaybackUrl: string = record?.user?.streamingInfo?.playbackUrl;
        const isValidPlaybackUrl = hasPlaybackUrl && typeof hasPlaybackUrl === 'string' && hasPlaybackUrl.length > 0;

        let interval: number | false;
        let reason: string;

        // Si le stream est terminé et qu'on a déjà le replay, pas besoin de refetch
        if (showEndedView && replayUrl) {
          interval = false;
          reason = 'STOPPED (stream ended + replay loaded)';
        }
        // Si le stream est actif, on fait un polling fréquent pour détecter la fin
        else if (isBroadcasting === true) {
          interval = 2000;
          reason = 'LIVE (2s - broadcasting)';
        }
        // Si on attend le broadcast (alerte créée mais pas de playbackUrl)
        // Polling fréquent pour détecter rapidement le démarrage du broadcast
        else if (record && !isValidPlaybackUrl) {
          interval = 1500;
          reason = 'WAITING (1.5s - no playback URL)';
        }
        // Si on attend le replay ou autres cas
        else {
          interval = hasPlaybackUrl ? 3000 : 2000;
          reason = hasPlaybackUrl ? 'STANDBY (3s - has URL)' : 'LOADING (2s - no URL)';
        }

        // Log when polling state changes
        const currentState = `${reason}|${interval}`;
        if (lastPollingStateRef.current !== currentState) {
          logger.log('⏱️ Polling interval changed', {
            reason,
            interval: interval === false ? 'stopped' : `${interval}ms`,
            isBroadcasting,
            hasPlaybackUrl: !!hasPlaybackUrl,
            showEndedView,
            hasReplayUrl: !!replayUrl
          });
          lastPollingStateRef.current = currentState;
        }

        return interval;
      },
      refetchIntervalInBackground: false,
      staleTime: 0,
      retry: (failureCount, error) => {
        if ((error as any)?.response?.status === 404 && failureCount < 5) {
          return true;
        }
        return failureCount < 3;
      },
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 3000),
    }
  });

  const { data, isLoading, isError } = queryResult;

  const isSmallOrLess = useMediaQuery(theme.breakpoints.down("sm"));
  const record = data?.data;
  const [duration, setDuration] = React.useState("0");

  // Log data fetch results
  React.useEffect(() => {
    const elapsedSinceMount = Date.now() - mountTimeRef.current;
    if (isLoading) {
      logger.log('⏳ Data loading...', { alertId: id, elapsedSinceMount });
    } else if (isError) {
      logger.error('❌ Data fetch error', { alertId: id, elapsedSinceMount });
    } else if (record) {
      logger.log('✅ Data fetched successfully', {
        alertId: id,
        elapsedSinceMount,
        isBroadcasting: record.isBroadcasting,
        isStreamReallyActive: record.isStreamReallyActive,
        hasPlaybackUrl: !!record.user?.streamingInfo?.playbackUrl,
        playbackUrl: record.user?.streamingInfo?.playbackUrl?.substring(0, 80),
        streamId: record.streamId,
        status: record.status
      });
    }
  }, [isLoading, isError, record, id]);

  // Reverse geocoding pour l'adresse - se met à jour à chaque refresh des coordonnées
  const { address, loading: addressLoading } = useGeocoding(
    record?.latitude,
    record?.longitude,
    { priority: 1 }
  );

  // Variables dérivées pour le streaming
  const hasPlaybackUrl = record?.user?.streamingInfo?.playbackUrl;
  const isStreamActive = record?.isBroadcasting === true;
  const isValidPlaybackUrl = hasPlaybackUrl && typeof hasPlaybackUrl === 'string' && hasPlaybackUrl.length > 0;

  // 🔧 Nouveau: Utiliser isStreamReallyActive du backend pour savoir si le stream IVS diffuse vraiment
  const isStreamReallyActive = record?.isStreamReallyActive === true;

  // Le stream est considéré comme terminé uniquement si :
  // 1. isBroadcasting est explicitement false ET
  // 2. Il y a une updatedAt différente de createdAt (indiquant une fin explicite)
  const isStreamEnded = record?.isBroadcasting === false &&
                        record?.updatedAt &&
                        record?.createdAt &&
                        record?.updatedAt !== record?.createdAt;

  // Détection de l'état "En attente du livestream"
  // Seulement si pas de playbackUrl (channel pas encore créé)
  // Note: On ne vérifie plus isStreamReallyActive pour permettre les vidéos de démo
  const isWaitingForBroadcast = record && !isStreamEnded && !isValidPlaybackUrl;

  // Log stream state for debugging "stuck" issues
  React.useEffect(() => {
    if (record) {
      const streamState = {
        isBroadcasting: record.isBroadcasting,
        isStreamActive,
        isStreamEnded,
        isStreamReallyActive,
        isValidPlaybackUrl,
        isWaitingForBroadcast,
        hasPlaybackUrl: !!hasPlaybackUrl,
        playerConnected,
        playerError,
        showEndedView,
        streamHasEnded,
        replayUrl: replayUrl ? 'present' : null,
        isLoadingReplay
      };

      // Determine the current UI state
      let uiState = 'unknown';
      if (record.isBroadcasting === false) {
        if (replayUrl) uiState = 'REPLAY';
        else if (hasPlaybackUrl && (isLoadingReplay || (!replayUrl && !replayError && record.streamId))) uiState = 'LOADING_REPLAY';
        else uiState = 'STREAM_ENDED_NO_REPLAY';
      } else if (record.isBroadcasting === true && isValidPlaybackUrl) {
        uiState = 'LIVE_STREAM';
      } else if (isWaitingForBroadcast) {
        uiState = 'WAITING_FOR_BROADCAST';
      } else {
        uiState = 'LOADING_STATE';
      }

      logger.log('🎯 Stream state snapshot', { ...streamState, uiState });
    }
  }, [
    record, isStreamActive, isStreamEnded, isStreamReallyActive, isValidPlaybackUrl,
    isWaitingForBroadcast, hasPlaybackUrl, playerConnected, playerError, showEndedView,
    streamHasEnded, replayUrl, isLoadingReplay, replayError
  ]);

  // Rediriger vers alert-history quand le stream est terminé
  useEffect(() => {
    if (isStreamEnded && id) {
      logger.log('🔀 Stream ended, redirecting to alert-history...', { alertId: id });
      navigate(`/alert-history/${id}`, { replace: true });
    }
  }, [isStreamEnded, id, navigate]);

  // Move all useEffect hooks before conditional returns
  useEffect(() => {
    if (record?.createdAt) {
      const calculateDuration = () => {
        const newDuration = getDuration({
          createdAt: record.createdAt,
          endedAt: record.updatedAt,
          isActive: isStreamActive
        }, { format: 'minutes-seconds' });
        setDuration(newDuration);
      };

      // Calculer immédiatement
      calculateDuration();

      // Pour les streams actifs, mettre à jour toutes les secondes
      let intervalId: NodeJS.Timeout | null = null;
      if (isStreamActive) {
        intervalId = setInterval(calculateDuration, 1000);
      }

      return () => {
        if (intervalId) {
          clearInterval(intervalId);
        }
      };
    }
  }, [record?.createdAt, record?.updatedAt, isStreamActive]);

  // Synchroniser le status local avec les données du serveur
  useEffect(() => {
    if (record?.status) {
      setAlertStatus(record.status);
    }
  }, [record?.status]);

  // Company-based access control
  useEffect(() => {
    if (!isLoading && record && currentUser) {
      // Check if user can access the alert data (check if alert user is from same company or if VIGILANE user with hasVigilane)
      if (!canAccessUserData(currentUser, record.user as { id: number; entrepriseId?: number; hasVigilane?: boolean })) {
        navigate('/monitoring', {
          replace: true
        });
        return;
      }
    }
  }, [record, currentUser, isLoading, navigate]);

    // Fonction pour récupérer l'URL de replay avec retry
  const fetchReplayUrl = React.useCallback(async (streamId: string, retries: number = 3) => {
    if (!streamId || isLoadingReplay) {
      logger.log('⏭️ Skipping replay fetch', { streamId, isLoadingReplay });
      return;
    }

    logger.log('📹 Starting replay fetch', { streamId, maxRetries: retries });
    setIsLoadingReplay(true);
    setReplayError(null);
    setReplayAttempted(streamId);

    let attemptCount = 0;
    const maxAttempts = retries;
    const delayBetweenAttempts = 3000; // 3 secondes entre chaque tentative

    const attemptFetch = async (): Promise<string | null> => {
      try {
        attemptCount++;
        logger.log(`📹 Replay fetch attempt ${attemptCount}/${maxAttempts}...`, { streamId });

        // Toujours utiliser getRecordingUrl avec le streamId spécifique
        // pour récupérer le bon replay (et non le dernier replay du channel)
        const url = await getRecordingUrl(streamId);

        if (url) {
          logger.log('✅ Replay URL retrieved successfully', { url: url.substring(0, 80) });
          return url;
        }

        if (attemptCount < maxAttempts) {
          logger.log(`⏳ No replay available yet, retrying in ${delayBetweenAttempts}ms...`);
          await new Promise(resolve => setTimeout(resolve, delayBetweenAttempts));
          return attemptFetch();
        }

        return null;
      } catch (error) {
        logger.error(`❌ Replay fetch attempt ${attemptCount} failed:`, error);
        if (attemptCount < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, delayBetweenAttempts));
          return attemptFetch();
        }
        throw error;
      }
    };

    try {
      const url = await attemptFetch();
      if (url) {
        logger.log('✅ Replay URL set successfully');
        setReplayUrl(url);
      } else {
        logger.warn('⚠️ Replay not available after all retries');
        setReplayError("Le replay n'est pas encore disponible. Veuillez réessayer dans quelques instants.");
      }
    } catch (error) {
      logger.error('❌ Replay fetch failed with error:', error);
      setReplayError("Erreur lors du chargement du replay");
    } finally {
      setIsLoadingReplay(false);
    }
  }, [isLoadingReplay]);

  // Reset automatique des erreurs quand une nouvelle URL arrive
  useEffect(() => {
    if (isValidPlaybackUrl) {
      logger.log('🎥 Broadcast detected! Auto-connecting to stream...', {
        playbackUrl: hasPlaybackUrl?.substring(0, 80),
        isStreamActive
      });
      setPlayerError(null);
      setRetryCount(0);
      setIsInitialLoad(false);
      // Reset replay states when live stream becomes active again
      setReplayUrl(null);
      setReplayError(null);
      setReplayAttempted(null);
      // Reset le flag de fin de stream si le stream reprend
      if (isStreamActive) {
        logger.log('🔄 Resetting stream ended flags');
        setStreamHasEnded(false);
        setShowEndedView(false);
      }
    }
  }, [isValidPlaybackUrl, hasPlaybackUrl, isStreamActive]);

  // État précédent pour détecter les changements
  const [prevIsBroadcasting, setPrevIsBroadcasting] = React.useState<boolean | null>(null);

  // Détecter la fin du stream et récupérer le replay
  useEffect(() => {
    const emergencyBroadcastStreamId = record?.streamId;
    const currentIsBroadcasting = record?.isBroadcasting;

    // Logger tous les changements de isBroadcasting
    if (prevIsBroadcasting !== currentIsBroadcasting) {
      logger.log(`📡 isBroadcasting changed: ${prevIsBroadcasting} -> ${currentIsBroadcasting}`, {
        streamId: emergencyBroadcastStreamId,
        hasReplayUrl: !!replayUrl
      });
    }

    // Détecter le passage de true à false (arrêt du stream)
    if (prevIsBroadcasting === true && currentIsBroadcasting === false) {
      logger.log('🛑 STREAM STOPPED! Hiding live player immediately', {
        streamId: emergencyBroadcastStreamId
      });

      // Masquer IMMÉDIATEMENT le player live
      setStreamHasEnded(true);
      setShowEndedView(true);
      setPlayerConnected(false);

      // Lancer la récupération du replay
      if (emergencyBroadcastStreamId && !replayUrl) {
        logger.log('⏳ Scheduling replay fetch in 5 seconds...', { streamId: emergencyBroadcastStreamId });
        setTimeout(() => {
          fetchReplayUrl(emergencyBroadcastStreamId, 5);
        }, 5000);
      }
    }
    // Détecter le redémarrage du stream
    else if (prevIsBroadcasting === false && currentIsBroadcasting === true) {
      logger.log('🔄 Stream restarting! isBroadcasting: false -> true');
      setShowEndedView(false);
      setStreamHasEnded(false);
      setReplayUrl(null);
      setReplayError(null);
      setReplayAttempted(null);
    }

    // Mettre à jour l'état précédent
    if (currentIsBroadcasting !== undefined) {
      setPrevIsBroadcasting(currentIsBroadcasting);
    }
  }, [record?.isBroadcasting, record?.streamId, prevIsBroadcasting, replayUrl]);

  // Gestion de la visibilité de la page (similaire à MonitoringList)
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      logger.log(`👁️ Page visibility changed: ${isVisible ? 'VISIBLE' : 'HIDDEN'}`, {
        wasVisible: isPageVisible,
        willRefetch: isVisible && !!queryResult.refetch
      });
      setIsPageVisible(isVisible);

      if (isVisible && queryResult.refetch) {
        logger.log('🔄 Triggering data refetch due to visibility change');
        queryResult.refetch();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [queryResult, isPageVisible]);

  // Auto-refresh complémentaire de 10 secondes (similaire à MonitoringList)
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (isPageVisible && (playerConnected || showEndedView) && queryResult.refetch) {
      intervalId = setInterval(() => {
        queryResult.refetch();
      }, 10000); // 10 seconds
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isPageVisible, playerConnected, showEndedView, queryResult]);

  // Fonction retry avec délai progressif
  const retryVideoPlayback = React.useCallback(() => {
    if (retryCount < 3) {
      const newRetryCount = retryCount + 1;
      setRetryCount(newRetryCount);
      setPlayerError(null);
    }
  }, [retryCount]);

  // Fonction pour mettre à jour le status de l'alerte
  const updateAlertStatus = React.useCallback(async (newStatus: 'NEW' | 'ACCEPTED' | 'CLOSED' | 'CALLED') => {
    if (!id || isUpdatingStatus) return;

    setIsUpdatingStatus(true);
    try {
      await axiosInstance.put(`/alert/${id}/status`, {
        status: newStatus
      });

      setAlertStatus(newStatus);

      // Show success notification
      openNotification?.({
        type: "success",
        message: translate("monitoring.statusUpdated"),
        description: translate("monitoring.statusUpdateSuccess"),
      });

      // Rafraîchir les données pour s'assurer que tout est synchronisé
      if (queryResult.refetch) {
        queryResult.refetch();
      }
    } catch (error) {
      // Show error notification
      openNotification?.({
        type: "error",
        message: translate("monitoring.statusUpdateError"),
        description: translate("monitoring.statusUpdateErrorMessage"),
      });
      // En cas d'erreur, revenir au status précédent
      setAlertStatus(record?.status || 'NEW');
    } finally {
      setIsUpdatingStatus(false);
    }
  }, [id, isUpdatingStatus, queryResult, record?.status, openNotification]);

  // Callbacks du player IVS
  const handlePlayerLoad = React.useCallback(() => {
    logger.log('🎬 Player LOADED callback received', {
      previouslyConnected: playerConnected,
      hasPlaybackUrl: isValidPlaybackUrl,
      hasReplayUrl: !!replayUrl,
      elapsedSinceMount: Date.now() - mountTimeRef.current
    });
    setPlayerError(null);
    setPlayerConnected(true);
  }, [playerConnected, isValidPlaybackUrl, replayUrl]);

  const handlePlayerError = React.useCallback((error: string) => {
    logger.error('❌ Player ERROR callback received', {
      error,
      wasConnected: playerConnected,
      hasPlaybackUrl: isValidPlaybackUrl,
      hasReplayUrl: !!replayUrl,
      elapsedSinceMount: Date.now() - mountTimeRef.current
    });
    setPlayerError(error);
    setPlayerConnected(false);
  }, [playerConnected, isValidPlaybackUrl, replayUrl]);

  const handlePlayerStateChange = React.useCallback((state: string) => {
    logger.log('🔄 Player STATE change callback:', state, {
      currentlyConnected: playerConnected,
      isBroadcasting: record?.isBroadcasting,
      elapsedSinceMount: Date.now() - mountTimeRef.current
    });
    switch (state) {
      case 'Playing':
        setPlayerConnected(true);
        setPlayerError(null);
        break;
      case 'Ended':
        // Ne pas faire confiance à cet événement pour détecter la fin du stream
        // On se base uniquement sur isBroadcasting du record
        logger.log('⚠️ Player "Ended" event received (ignored - we rely on isBroadcasting)');
        break;
      case 'Idle':
        setPlayerConnected(false);
        break;
    }
  }, [playerConnected, record?.isBroadcasting]);

  // Fonction helper pour obtenir la couleur et le label du status
  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'NEW':
        return { color: 'info', label: translate('monitoring.status.new') };
      case 'ACCEPTED':
        return { color: 'warning', label: translate('monitoring.status.accepted') };
      case 'CALLED':
        return { color: 'secondary', label: translate('monitoring.status.called') };
      case 'CLOSED':
        return { color: 'success', label: translate('monitoring.status.closed') };
      default:
        return { color: 'default', label: status };
    }
  };

  // Don't render if access should be denied
  if (!isLoading && record && currentUser &&
      !canAccessUserData(currentUser, record.user as { id: number; entrepriseId?: number; hasVigilane?: boolean })) {
    return null; // Component will unmount due to navigation
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return <Alert severity="error">{translate("monitoring.loadError")}</Alert>;
  }

  const greenIcon = L.icon({
    iconUrl:
      "https://res.cloudinary.com/dlnimaboq/image/upload/v1702476006/oupbiebxhfkbc49lckoj.png",
    iconSize: [48, 48],
    iconAnchor: [24, 24],
    shadowAnchor: [4, 62],
    popupAnchor: [0, -20],
  });

  return (
    <Box
      sx={{
        display: "grid",
        width: "100%",
        height: "100%",
        gridTemplateColumns: {
          xs: "repeat(1, 1fr)",
          sm: "2fr 5fr",
          md: "2fr 5fr",
        },
        gap: 2,
      }}
    >
      <Card>
        <CardContent>
          <Box sx={{
            display: "flex",
            flexDirection: { xs: "column", xl: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", xl: "center" },
          }}>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: { xs: 0, lg: 2 } }}>
              <IconButton onClick={goBack}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6">
                {translate("alert.fields.title")}
              </Typography>
              <Typography variant="h6">{`#${record?.id ?? ""}`}</Typography>
            </Box>

            <Box sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              mb: 2,
              width: { xs: "100%", lg: "auto" }
            }}>
            <FormControl fullWidth size="small">
              <Select
                value={alertStatus}
                onChange={(e) => updateAlertStatus(e.target.value as any)}
                disabled={isUpdatingStatus}
                size="small"
              >
                <MenuItem value="NEW">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip label={translate("monitoring.status.new")} color="info" size="small" />
                  </Stack>
                </MenuItem>
                <MenuItem value="ACCEPTED">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip label={translate("monitoring.status.accepted")} color="warning" size="small" />
                  </Stack>
                </MenuItem>
                <MenuItem value="CALLED">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip label={translate("monitoring.status.called")} color="secondary" size="small" />
                  </Stack>
                </MenuItem>
                <MenuItem value="CLOSED">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip label={translate("monitoring.status.closed")} color="success" size="small" />
                  </Stack>
                </MenuItem>
              </Select>
            </FormControl>
            </Box>


          </Box>
          <Box
            sx={{
              width: "100%",
              // 9:16 aspect ratio (16/9 * 100)
              paddingTop: "177.77%",
              position: "relative",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
              }}
            >
              {/* Affichage basé sur isBroadcasting du record */}
              {record?.isBroadcasting === false ? (
                replayUrl ? (
                  // Afficher le replay si disponible
                  <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                    <IVSPlayer
                      key={`replay-${replayUrl}`}
                      streamUrl={replayUrl}
                      isLive={false}
                      controls={true}
                      autoplay={false}
                      onPlayerLoad={handlePlayerLoad}
                      onPlayerError={handlePlayerError}
                      onPlayerStateChange={handlePlayerStateChange}
                      style={{
                        width: "100%",
                        height: "100%",
                      }}
                    />
                    {/* Indicateur de replay */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.875rem',
                        fontWeight: 'bold',
                        zIndex: 10,
                      }}
                    >
                      📹 REPLAY
                    </Box>
                    {/* Message "Cliquez pour lire" au centre */}
                    {playerConnected === false && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          color: 'white',
                          px: 3,
                          py: 2,
                          borderRadius: 2,
                          fontSize: '1.2rem',
                          fontWeight: 'bold',
                          zIndex: 10,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          cursor: 'pointer',
                        }}
                      >
                        ▶️ Cliquez pour lire le replay
                      </Box>
                    )}
                  </Box>
                ) : hasPlaybackUrl && (isLoadingReplay || (!replayUrl && !replayError && record?.streamId)) ? (
                  // Garder le player live figé avec un overlay pendant la préparation du replay
                  <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                    <IVSPlayer
                      key={`frozen-${hasPlaybackUrl}`}
                      streamUrl={hasPlaybackUrl}
                      onPlayerLoad={handlePlayerLoad}
                      onPlayerError={handlePlayerError}
                      onPlayerStateChange={handlePlayerStateChange}
                      style={{
                        width: "100%",
                        height: "100%",
                      }}
                      controls={false}
                    />
                    {/* Overlay "Stream terminé" */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 20,
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: 'rgba(0, 0, 0, 0.8)',
                          borderRadius: 2,
                          p: 4,
                          textAlign: 'center',
                          maxWidth: '80%',
                        }}
                      >
                        <CircularProgress
                          size={60}
                          sx={{
                            color: 'white',
                            mb: 3
                          }}
                        />
                        <Typography variant="h5" sx={{ color: 'white', mb: 2, fontWeight: 'bold' }}>
                          Stream terminé
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: '#ccc',
                          }}
                        >
                          {isLoadingReplay
                            ? "Chargement du replay en cours..."
                            : "Préparation du replay, veuillez patienter..."}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  // Stream terminé sans replay disponible ou erreur
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : "#f5f5f5",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "10px",
                      p: 2,
                    }}
                  >
                    <Typography variant="h5" color="text.primary" sx={{ mb: 2 }}>
                      {translate("monitoring.streamEnded")}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      textAlign="center"
                      sx={{ mb: 2 }}
                    >
                      {replayError || translate("monitoring.noReplayAvailable")}
                    </Typography>
                    {duration && duration !== "0" && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        {translate("monitoring.duration")} : {duration}
                      </Typography>
                    )}
                  </Box>
                )
              ) : record?.isBroadcasting === true && isValidPlaybackUrl ? (
                // Stream actif avec playbackUrl - afficher le player live
                // Note: isStreamReallyActive retiré pour permettre les vidéos de démo (Cloudinary)
                <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                  <IVSPlayer
                    key={`live-${hasPlaybackUrl}`}
                    streamUrl={hasPlaybackUrl}
                    onPlayerLoad={handlePlayerLoad}
                    onPlayerError={handlePlayerError}
                    onPlayerStateChange={handlePlayerStateChange}
                    style={{
                      width: "100%",
                      height: "100%",
                    }}
                    controls={true}
                    autoplay={true}
                  />
                  {/* Badge LIVE */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 10,
                      left: 10,
                      backgroundColor: '#ff0000',
                      color: 'white',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.875rem',
                      fontWeight: 'bold',
                      zIndex: 10,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: 'white',
                        animation: 'pulse 1.5s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%, 100%': {
                            opacity: 1,
                          },
                          '50%': {
                            opacity: 0.3,
                          },
                        },
                      }}
                    />
                    🔴 LIVE
                  </Box>
                </Box>
              ) : isWaitingForBroadcast ? (
                // En attente du livestream - afficher un overlay explicatif
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[900] : "#1a1a1a",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "10px",
                    p: 3,
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: 2,
                      p: 4,
                      textAlign: 'center',
                      maxWidth: '80%',
                      border: '2px dashed rgba(255, 255, 255, 0.3)',
                    }}
                  >
                    <CircularProgress
                      size={60}
                      sx={{
                        color: theme.palette.mode === "dark" ? theme.palette.primary.light : theme.palette.primary.main,
                        mb: 3
                      }}
                    />
                    <Typography variant="h5" sx={{ color: 'white', mb: 2, fontWeight: 'bold' }}>
                      {translate("monitoring.waitingForBroadcast")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#ccc',
                        mb: 2,
                      }}
                    >
                      {translate("monitoring.waitingForBroadcastDescription")}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#999',
                        fontSize: '0.875rem',
                      }}
                    >
                      {translate("monitoring.waitingForBroadcastHint")}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[900] : "#000",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "10px",
                    color: theme.palette.mode === "dark" ? theme.palette.common.white : "#fff",
                    p: 2,
                  }}
                >
                  <CircularProgress
                    size={60}
                    sx={{
                      color: theme.palette.mode === "dark" ? theme.palette.primary.light : "#fff",
                      mb: 2
                    }}
                  />
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {playerError
                      ? translate("monitoring.connectionError")
                      : isValidPlaybackUrl
                      ? translate("monitoring.connectingToStream")
                      : translate("monitoring.creatingAlert")}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.mode === "dark" ? theme.palette.grey[300] : "#ccc",
                      textAlign: "center"
                    }}
                  >
                    {playerError
                      ? translate("monitoring.checkConnection")
                      : isValidPlaybackUrl
                      ? translate("monitoring.loadingVideoStream")
                      : translate("monitoring.preparingBroadcast")}
                  </Typography>
                  {playerError && retryCount < 3 && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.mode === "dark" ? theme.palette.error.light : "#ff6b6b",
                        mt: 1,
                        textAlign: "center"
                      }}
                    >
                      {translate("monitoring.automaticReconnection")}
                    </Typography>
                  )}
                  {playerError && retryCount >= 3 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.mode === "dark" ? theme.palette.error.light : "#ff6b6b",
                          mb: 1,
                          textAlign: "center"
                        }}
                      >
                        {playerError}
                      </Typography>
                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <button
                          onClick={retryVideoPlayback}
                          style={{
                            backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[700] : "#333",
                            color: theme.palette.mode === "dark" ? theme.palette.common.white : "#fff",
                            border: "none",
                            padding: "8px 16px",
                            borderRadius: "4px",
                            cursor: "pointer",
                          }}
                        >
                          {translate("monitoring.retry")}
                        </button>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
      <Stack
        spacing={2}
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Card sx={{ flexShrink: 0 }}>
          <CardContent>
            <Stepper
              nonLinear
              activeStep={isStreamActive ? 3 : isStreamEnded ? 4 : 2}
              orientation={isSmallOrLess ? "vertical" : "horizontal"}
            >
              <Step key={1}>
                <StepLabel
                  optional={
                    <Typography variant="caption">
                      {record?.createdAt &&
                        dayjs(record?.createdAt).format("DD/MM/YYYY HH:mm")}
                    </Typography>
                  }
                  error={record?.createdAt === ""}
                >
                  {translate("alert.fields.alarm-started")}
                </StepLabel>
              </Step>
              <Step key={2}>
                <StepLabel
                  optional={
                    <Typography variant="caption">
                      {isStreamActive
                        ? translate("alert.fields.is-broadcasting")
                        : isStreamEnded
                        ? translate("alert.fields.duree") + " " + duration
                        : translate("monitoring.waiting")}
                    </Typography>
                  }
                  error={record?.isBroadcasting === null}
                >
                  {translate("alert.fields.is-broadcasting")}
                </StepLabel>
              </Step>
              <Step key={3}>
                <StepLabel
                  optional={
                    <Typography variant="caption">
                      {record?.updatedAt &&
                        dayjs(record?.updatedAt).format("DD/MM/YYYY HH:mm")}
                    </Typography>
                  }
                  error={record?.updatedAt === ""}
                >
                  {translate("alert.fields.tracking-ended")}
                </StepLabel>
              </Step>
            </Stepper>
          </CardContent>
        </Card>

        <Stack
          direction={isSmallOrLess ? "column" : "row"}
          spacing={2}
          alignItems="stretch"
          sx={{ flexShrink: 0 }}
        >
          {/* Section Utilisateur et Téléphone */}
          <Paper
            sx={{
              padding: 2,
              flex: 2,
              minWidth: { xs: "100%", sm: "300px" },
            }}
          >
            <Stack
              direction="row"
              flexWrap="wrap"
              alignItems="center"
              justifyContent={isSmallOrLess ? "center" : "space-between"}
              spacing={2}
            >
              <Stack
                direction={isSmallOrLess ? "column" : "row"}
                alignItems={"center"}
                textAlign={isSmallOrLess ? "center" : "left"}
                gap={2}
              >
                <Avatar
                  alt={record?.user?.fullname}
                  src={record?.user?.profilePicture}
                  sx={{ width: 100, height: 100 }}
                />
                <Box>
                  <Typography>{translate("monitoring.user")}</Typography>
                  <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography variant="h6">
                      {record?.user?.fullname || record?.user?.email}
                    </Typography>
                    {record?.user?.subGroup?.name && (
                      <Chip
                        icon={<AccountTreeIcon fontSize="small" />}
                        label={record.user.subGroup.name}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: '16px',
                          '& .MuiChip-icon': {
                            color: 'primary.main',
                          },
                        }}
                      />
                    )}
                  </Stack>
                  {record?.latitude && record?.longitude && !addressLoading && address && (
                    <Stack direction="row" alignItems="center" gap={0.5} sx={{ mt: 0.5 }}>
                      <LocationOnIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {address}
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Stack>
              <Stack
                direction="row"
                gap={2}
                flexWrap="wrap"
                justifyContent="center"
                alignItems="center"
                sx={{
                  p: 2,
                  backgroundColor: theme.palette.mode === "dark" ? theme.palette.success.dark : "#36B37E",
                  borderRadius: 2,
                  color: theme.palette.mode === "dark" ? theme.palette.common.white : "#fff",
                }}
              >
                <Phone size={36} />
                <div>
                  <Typography>{translate("monitoring.phoneNumber")}</Typography>
                  <Typography variant="h6">{record?.user?.phone}</Typography>
                </div>
              </Stack>
            </Stack>
          </Paper>
        </Stack>

        <Paper sx={{ height: "100%", overflow: "hidden" }}>
          <Box
            sx={{
              flex: 1,
              p: 2,
              borderBottom: `1px solid ${theme.palette.mode === "dark" ? theme.palette.divider : "#e0e0e0"}`,
              minHeight: 0,
              height: "100%",
            }}
          >
            {record?.latitude && record?.longitude && (
              <MapContainer
                center={[record?.latitude, record?.longitude]}
                zoom={20}
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.stadiamaps.com/" target="_blank">Stadia Maps</a> &copy; <a href="https://openmaptiles.org/" target="_blank">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url={`https://tiles.stadiamaps.com/tiles/osm_bright/{z}/{x}/{y}{r}.png?api_key=${process.env.REACT_APP_STADIA_API_KEY}`}
                  minZoom={0}
                  maxZoom={20}
                />
                <Marker
                  icon={greenIcon}
                  position={[record?.latitude, record?.longitude]}
                >
                  <Popup>
                    <a
                      href={`https://www.google.com/maps?q=${record?.latitude},${record?.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        textDecoration: "none",
                        color: theme.palette.mode === "dark" ? theme.palette.primary.light : theme.palette.primary.main
                      }}
                    >
                      {translate("monitoring.openInGoogleMaps")}
                    </a>
                  </Popup>
                </Marker>
              </MapContainer>
            )}
          </Box>
        </Paper>
      </Stack>
    </Box>
  );
};
